/* Description images are deliberately inert in Liquid, including on translated pages. */
(() => {
  'use strict';
  const seen = new WeakSet();
  const near = new Set();
  const pending = new Set();
  const active = new Set();
  let backgroundActive = false;
  let backgroundScheduled = false;
  const hosted = source => {
    try { const url = new URL(source, location.href); return url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/files/'); }
    catch { return false; }
  };
  function responsive(source, image) {
    if (!hosted(source) || /\.svg(?:\?|$)/i.test(source)) return source;
    const natural = Number(image.dataset.descriptionWidth);
    if (!natural) return source; // Do not change intrinsic display size when metadata is unknown.
    const url = new URL(source, location.href);
    const width = image.getBoundingClientRect().width || image.parentElement?.clientWidth || window.innerWidth;
    const requested = Math.ceil(width * Math.min(window.devicePixelRatio || 1, 2));
    // Width buckets are reusable across visits and never upscale a known original.
    const bucket = [360, 540, 720, 960, 1200, 1600, 2000].find(value => value >= requested) || 2000;
    url.searchParams.set('width', String(natural > 0 ? Math.min(natural, bucket) : bucket));
    return url.href;
  }
  async function activate(image) {
    const original = image.dataset.descriptionOriginal || image.dataset.descriptionSrc;
    let source = image.dataset.descriptionSrc;
    if (!source) return;
    if (!image.hasAttribute('data-description-localized') && window.ShopstaLocalizedImage) {
      try { source = await window.ShopstaLocalizedImage(source) || original; }
      catch { source = original; }
    }
    if (!image.isConnected) return;
    image.removeAttribute('srcset');
    // This image was selected either by proximity or the bounded idle queue.
    image.loading = 'eager';
    await new Promise(resolve => {
      let fallback = false;
      const finish = () => {
        clearTimeout(timeout);
        image.onload = image.onerror = null;
        if (!image.hasAttribute('width') && !image.hasAttribute('height') && image.naturalWidth) {
          image.width = image.naturalWidth; image.height = image.naturalHeight;
        }
        delete image.dataset.descriptionSrc;
        delete image.dataset.descriptionSrcset;
        resolve();
      };
      image.onload = finish;
      image.onerror = () => {
        if (!fallback) {
          fallback = true;
          // Unmodified original is the last resort, including failed CDN resizing.
          image.src = original;
        } else finish();
      };
      const timeout = setTimeout(finish, 15000);
      image.src = responsive(source, image);
    });
  }
  function start(image, background = false) {
    active.add(image);
    pending.delete(image);
    near.delete(image);
    observer?.unobserve(image);
    if (background) backgroundActive = true;
    image.fetchPriority = background ? 'low' : 'auto';
    activate(image).finally(() => {
      active.delete(image);
      if (background) backgroundActive = false;
      requestAnimationFrame(() => { drain(); scheduleBackground(); });
    });
  }
  function scheduleBackground() {
    const apps = window.ShopstaApps;
    if (backgroundScheduled || backgroundActive || active.size >= 2 || !pending.size || !apps?.onProductIdle || !apps.backgroundAllowed()) return;
    backgroundScheduled = true;
    apps.onProductIdle(() => {
      backgroundScheduled = false;
      if (!apps.backgroundAllowed()) return;
      drain();
      if (backgroundActive || active.size >= 2) return;
      for (const image of pending) {
        if (!image.isConnected || !image.dataset.descriptionSrc) { pending.delete(image); continue; }
        start(image, true);
        break;
      }
    });
  }
  function drain() {
    for (const image of near) {
      // Keep startup serial; reserve a second slot only for a foreground image during idle preparation.
      if (active.size >= (backgroundActive ? 2 : 1)) break;
      near.delete(image);
      if (!image.isConnected || !image.dataset.descriptionSrc || active.has(image)) continue;
      const bounds = image.getBoundingClientRect();
      if (observer && (bounds.top > window.innerHeight + 600 || bounds.bottom < -600)) continue;
      start(image);
      // Unknown dimensions can put later images falsely inside the loading margin.
      if (!image.hasAttribute('width') || !image.hasAttribute('height')) break;
    }
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) entry.isIntersecting ? near.add(entry.target) : near.delete(entry.target);
    drain();
  }, { rootMargin: '600px 0px' }) : null;
  function scan(root = document) {
    root.querySelectorAll('[data-description-image]').forEach(image => {
      if (seen.has(image)) return;
      seen.add(image);
      pending.add(image);
      image.hidden = false;
      // Preserve any merchant height styling, otherwise scale reserved dimensions naturally.
      if (!image.style.height) image.style.height = 'auto';
      if (observer) observer.observe(image);
      else near.add(image);
    });
    if (!observer) drain();
    scheduleBackground();
  }
  scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopsta:description-ready', event => scan(event.target));
})();
